// The shared sink for every v-html in this package: Showdown-rendered markdown,
// rich-text editor content, and any other markup we did not author ourselves
// (persisted policy descriptions, review notes, change logs, rationales, …).
//
// WHY THIS EXISTS RATHER THAN `import DOMPurify from 'dompurify'`:
// dompurify's default export is a FACTORY that binds itself to a global
// `window` if one happens to exist when the module is evaluated. Where one does
// not, it yields an instance with `isSupported === false` whose `sanitize` is a
// PASSTHROUGH — it returns the markup untouched, silently. Module evaluation
// order is not something a component should be betting its XSS defence on, and
// the failure is invisible: no error, no warning, just unsanitized HTML.
//
// That is not hypothetical — see apps/admin/app/utils/policy-studio/sanitize.ts
// for the incident that motivated binding explicitly instead. Binding lazily
// against `window` makes the behaviour independent of both the dompurify
// version and module evaluation order.
import createDOMPurify, { type DOMPurify } from 'dompurify'

let purifier: DOMPurify | null = null

/**
 * Sanitize untrusted HTML for `v-html`. Client-only by construction.
 *
 * THROWS without a DOM instead of returning the input. A sanitizer that
 * quietly does nothing is worse than one that fails: the caller renders the
 * result either way, so silence here means shipping the payload.
 */
export function sanitizeHtml(html: string): string {
  if (!purifier) {
    if (typeof window === 'undefined') {
      throw new Error(
        'sanitizeHtml: no DOM available — this must run client-side. ' +
          'Refusing to return unsanitized HTML.',
      )
    }
    purifier = createDOMPurify(window)
  }
  return purifier.sanitize(html)
}
